import { observer } from "mobx-react-lite";
import {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PropsWithChildren } from "react";
import {
  assert,
  EventBusManager,
  WS_UPGRADE_PATH,
  type WebSocketAppEvent,
} from "utils";

import { useWebSocket } from "src/hooks/useWebSocket";

import { useAuthStore } from "../auth/AuthContext";

interface EventBusContextValue {
  eventBus: EventBusManager<WebSocketAppEvent>;
  reconnectCount: number;
}

const EventBusContext = createContext<EventBusContextValue | null>(null);

function getWebSocketUrl(): string {
  const apiBaseUrl = import.meta.env.VITE_APP_API_BASE_URL || "";
  const url = new URL(apiBaseUrl);

  const protocol = url.protocol === "https:" ? "wss:" : "ws:";
  const fullUrl = `${protocol}//${url.host}${WS_UPGRADE_PATH}`;

  return fullUrl;
}

export const EventBusProvider = observer(function EventBusProvider(
  props: PropsWithChildren,
) {
  const authStore = useAuthStore();
  const [eventBus] = useState(() => {
    return new EventBusManager<WebSocketAppEvent>();
  });
  const [reconnectCount, setReconnectCount] = useState(0);
  const hasOpenedOnceRef = useRef(false);

  useEffect(
    function resetInitialOpenGateOnDisconnect() {
      if (!authStore.isAuthenticated) {
        hasOpenedOnceRef.current = false;
      }
    },
    [authStore.isAuthenticated],
  );

  const socketUrl =
    authStore.isAuthenticated && typeof window !== "undefined"
      ? getWebSocketUrl()
      : null;

  useWebSocket<WebSocketAppEvent>(
    socketUrl,
    {
      shouldReconnect: (closeEvent) => {
        // Not authenticated
        if (!authStore.isAuthenticated) {
          return false;
        }

        // Auth failures (1008 = policy violation)
        if (closeEvent.code === 1008) {
          return false;
        }

        return true;
      },
      reconnectAttempts: Number.POSITIVE_INFINITY,
      reconnectInterval: (lastAttemptNumber) => {
        return Math.min(30_000, 1000 * 2 ** lastAttemptNumber);
      },
      onOpen: () => {
        if (!hasOpenedOnceRef.current) {
          hasOpenedOnceRef.current = true;
          return;
        }

        setReconnectCount((prev) => {
          return prev + 1;
        });
      },
      onMessage: (event) => {
        try {
          const data = JSON.parse(event.data) as WebSocketAppEvent;
          eventBus.dispatch(data);
        } catch (err) {
          console.error("[WS_CLIENT_PARSE_OR_DISPATCH]", err);
        }
      },
      onError: (error) => {
        console.warn("[WS_CLIENT_ERROR]", error);
      },
    },
    !!socketUrl,
  );

  const contextValue = useMemo(() => {
    return { eventBus, reconnectCount };
  }, [eventBus, reconnectCount]);

  return (
    <EventBusContext.Provider value={contextValue}>
      {props.children}
    </EventBusContext.Provider>
  );
});

export function useEventBus(): EventBusManager<WebSocketAppEvent> {
  const context = useContext(EventBusContext);
  assert(context !== null, "useEventBus must be used within EventBusProvider");
  return context.eventBus;
}

export function useReconnectCount(): number {
  const context = useContext(EventBusContext);
  assert(
    context !== null,
    "useReconnectCount must be used within EventBusProvider",
  );
  return context.reconnectCount;
}

export function useEventSubscription<T extends WebSocketAppEvent["type"]>(
  eventType: T,
  handler: (event: Extract<WebSocketAppEvent, { type: T }>) => void,
): void {
  const eventBus = useEventBus();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    function stableHandler(event: Extract<WebSocketAppEvent, { type: T }>) {
      return handlerRef.current(event);
    }
    const unsubscribe = eventBus.subscribe(eventType, stableHandler);
    return unsubscribe;
  }, [eventBus, eventType]);
}

export function useOnWSReconnect(handler: () => void): void {
  const reconnectCount = useReconnectCount();
  const onReconnect = useEffectEvent(handler);

  useEffect(() => {
    if (reconnectCount === 0) {
      return;
    }

    onReconnect();
  }, [reconnectCount]);
}
